import * as React from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { Button } from '@shared/ui-react/Button';
import { cs } from '@shared/utils/utils-classname';
import { FormErrors } from '@shared/ui-react/UtilComponents';
import { InputNumber } from 'primereact/inputnumber';
import { Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { WorkflowsService } from '@shared/services/WorkflowsService';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Dropdown } from 'primereact/dropdown';

@observer
export class WorkflowReviewTaskEditDialog extends React.Component<{
    task?: IWorkflowTask;
    onSuccess: () => void;
    onClose: () => void;
    initialFileUuids?: string[];
}> {
    @observable task: Partial<IWorkflowTask> = {
        name: '',
        timeAllowed: 3,
        isWorkdaysOnly: true,
        documents: [] as string[],
        workflowUuid: null,
        type: 'REVIEW',
    };

    @observable isLoading = false;
    @observable isSaving = false;
    @observable errors: Record<string, { type: string }> = {};
    @observable isLoadingTree = false;
    @observable.ref treeData: ITreeData;
    @observable.ref workflows: IWorkflow[] = [];

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetchWorkflows();

        if (this.props.task) {
            const raw = JSON.parse(JSON.stringify(this.props.task));
            WorkflowsService.actorsFlatten(raw);
            this.task = raw;

            if (this.task.documents?.length) {
                this.fetchTree();
            }
        } else if (this.props.initialFileUuids) {
            this.fetchTree().then(() => {
                this.task.documents = this.props.initialFileUuids as any;
            });
        }
    }

    fetchWorkflows = async () => {
        this.isLoading = true;
        try {
            const response = await Ajax2({
                url: kuura.getContextPath() + '/api/workflow/all',
                method: 'GET',
                params: {
                    projectUuid: kuura.getProjectUuid(),
                },
            });
            this.workflows = response.workflowList ?? [];
        } catch (e) {
            console.log('fetchAll error:', e);
        } finally {
            this.isLoading = false;
        }
    };

    fetchTree = async () => {
        if (this.treeData || this.isLoadingTree) return;
        this.isLoadingTree = true;
        this.treeData = await FileTreeService.fetchFileTree();
        this.isLoadingTree = false;
    };

    save = async () => {
        const raw = JSON.parse(JSON.stringify(this.task));
        WorkflowsService.actorsUnflatten(raw);

        this.isSaving = true;
        try {
            const urlBase = `${kuura.getContextPath()}/api/1/tasks`;
            const url = this.isEdit ? `${urlBase}/edit` : urlBase + '/workflow-task';

            const response = await Ajax2({
                url: url,
                method: this.isEdit ? 'PUT' : 'POST',
                contentType: 'json',
                data: {
                    ...raw,
                    projectUuid: kuura.getProjectUuid(),
                },
            });
            this.props.onSuccess();
        } catch (e) {
            Notify.error(e.response?.data?.title ?? e.response?.data?.message);
            console.log('save task error:', e);
        } finally {
            this.isSaving = false;
        }
    };

    addFile = () => {
        this.fetchTree();
        this.task.documents.push(null);
    };

    changeDoc = (uuid: string, index: number) => {
        if (uuid == null) {
            this.task.documents.splice(index, 1);
        } else {
            this.task.documents[index] = uuid;
        }
    };

    get isEdit() {
        return !!this.props.task?.uuid;
    }

    get canSave() {
        return !this.isSaving && this.task.workflowUuid;
    }

    render() {
        return (
            <>
                <div className={cs('card padded form-container flex-column gap of-a', { 'overlay-loading': this.isSaving || this.isLoading })}>
                    <div className='form-group' style={{ maxWidth: 500 }}>
                        <label>{__('general.name')} *</label>
                        <input type='text' value={this.task.name} onChange={e => (this.task.name = e.currentTarget.value)} disabled={this.isSaving} maxLength={255} />
                        <FormErrors error={this.errors.name} />
                    </div>

                    <div className='form-group' style={{ maxWidth: 500 }}>
                        <label>{__('workflows.workflow_singular')} *</label>
                        <Dropdown
                            value={this.task.workflowUuid}
                            options={this.workflows}
                            onChange={e => (this.task.workflowUuid = e.value)}
                            optionLabel='name'
                            optionValue='uuid'
                            filter
                            disabled={this.isEdit || this.isSaving || this.isLoading}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ maxWidth: 500 }} className={cs('form-group', { 'fade-disable': this.isLoadingTree })}>
                        <label>{__('general.documents')}</label>
                        <div className='workflow-documents-list'>
                            {this.isLoadingTree
                                ? null
                                : this.task.documents.map((uuid, index: number) => (
                                      <FiletreeSelectReact
                                          key={uuid}
                                          selectable='file'
                                          treeData={this.treeData}
                                          useUuid={true}
                                          selectedId={uuid}
                                          disabledUuids={this.task.documents ?? []}
                                          hideEmptyFolders={true}
                                          canClearValue={true}
                                          showSelectedIcon={false}
                                          hideNonPresentable={true}
                                          childFilter={() => true}
                                          onChange={e => this.changeDoc(e, index)}
                                      />
                                  ))}
                        </div>
                    </div>

                    <div className='flex-row gap'>
                        <Button
                            primary
                            subtle
                            icon='iki iki-file'
                            onClick={this.addFile}
                            loading={this.isLoadingTree}
                            disabled={this.isSaving || this.isLoading || this.task.documents.includes(null)}
                        >
                            {__('workflows.tasks.link_a_file')}
                        </Button>
                    </div>

                    <div className='form-group'>
                        <label>{__('workflows.time_allowed')}</label>
                        <div className='flex-row gap-half'>
                            <InputNumber
                                value={this.task.timeAllowed}
                                onChange={e => (this.task.timeAllowed = e.value)}
                                disabled={this.isEdit || this.isSaving || this.isLoading}
                                allowEmpty={false}
                                min={1}
                            />
                            <select
                                value={this.task.isWorkdaysOnly ? 'workdays' : 'days'}
                                onChange={e => (this.task.isWorkdaysOnly = e.currentTarget.value === 'workdays')}
                                style={{ width: 'auto', height: 'auto' }}
                                disabled={this.isEdit || this.isSaving || this.isLoading}
                            >
                                <option value='workdays'>
                                    {__('workflows.time_allowed.workdays')} ({__('workflows.time_allowed.workdays_description')})
                                </option>
                                <option value='days'>{__('workflows.time_allowed.days')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <footer className='flex-row gap-half padded' style={{ justifyContent: 'flex-end' }}>
                    <Button onClick={this.props.onClose} disabled={this.isSaving} secondary subtle>
                        {__('general.cancel')}
                    </Button>
                    <Button onClick={this.save} disabled={!this.canSave} primary>
                        {__(this.isEdit ? 'general.update' : 'general.create')}
                    </Button>
                </footer>
            </>
        );
    }
}
